<!--
Map builder room admin screen.
@author crm4f
-->

<template>
  
  <div class="featureAdminContainer">
    <div class="featureOptionsContainer">

      <div class="btn-group" role="group" aria-label="newItemOptions">
        <button type="button" :disabled="currentFeature == null || currentFeature?.featureName == null" class="btn btn-primary active btn-sm" @click="saveCurrentFeature()">Save</button>
        <button type="button" :disabled="currentFeature == null" class="btn btn-primary active btn-sm" @click="clearCurrentFeature()">Cancel</button>
        <button type="button" :disabled="currentFeature == null" class="btn btn-primary active btn-sm" @click="deleteCurrentFeature()">Delete</button>
      </div>

      <!-- NEW FEATURE -->
      <div class="optionRow">
        <div style="width: fit-content; justify-content: center; display: flex;">
          <button 
            style="width:auto !important" 
            class="btn btn-primary active btn-sm" 
            @click="createNewFeature()">
            New Feature
          </button>

          <select
            :disabled="currentFeature?.featureId == 0"
            @change="featureTypeChanged()"
            class="form-control-sm" 
            v-model="currentFeatureType">
            <option v-for="(featureType ,i) in featureTypes" :value="featureType" :key="i">{{ featureType }}</option>
          </select>
        </div>

        <select 
          @change="featureChanged()"
          :disabled="currentFeatureType == null || currentFeature?.featureId == 0" 
          :class="{'inputDisabled' :currentFeatureType == null}" 
          class="form-control-sm" 
          v-model="currentFeature">
          <option v-for="(feature,i) in features" :value="feature" :key="i">{{ feature.featureName }}</option>
        </select>
        <br>
      </div>

      <div v-if="currentFeature !== null">

        <!-- FEATURE ID -->
        <div class="optionRow">
          <div>
            <label for="id">Id:</label>
          </div>
          <input 
            id="id"
            :disabled="true" 
            :class="{'inputDisabled' :currentFeature == null}" 
            class="form-control-sm" 
            v-model="currentFeature.featureId"
            label="id"
          />
          <br>
        </div>

        <!-- FEATURE NAME -->
        <div class="optionRow">
          <div>
            <label for="name">Name:</label>
          </div>
          <input 
            label="name"
            id="name"
            :disabled="currentFeature == null"
            :class="{'inputDisabled' :currentFeature == null}"
            class="form-control-sm"
            v-model="currentFeature.featureName"
            required
          />
          <br>
        </div>

        <!-- TYPE -->
        <div class="optionRow">
          <div>
            <label for="type">Type:</label>
          </div>
          <select  
            v-model="currentFeature.featureType"
            label="type"
            id="type"
            :disabled="currentFeature == null" 
            :class="{'inputDisabled' :currentFeature == null}" 
            class="form-control-sm" >
            <option v-for="(featureType ,i) in featureTypes" 
              :value="featureType"
              :key="i"
            >
              {{ featureType }}
            </option>
          </select>
          <br>
        </div>

        <!-- INFO -->
        <div class="optionRow">
          <div>
            <label for="dirs">Info:</label>
          </div>
          <textarea
            id="info"
            :disabled="currentFeature == null"
            :class="{'inputDisabled' :currentFeature == null}" 
            class="form-control-sm"
            v-model="currentFeature.featureInfo"  
            label="info"
          />
          <br>
        </div>

      </div>

      <div style="color: #003865; margin-top: 25%" v-else>
        <h1 class="display-5">Select or create a feature to begin editing...</h1>
        <font-awesome-icon style="font-size: 50px;" icon="fa-solid fa-icons" />
      </div>

    </div>

    <div class="gmapContainer">
      <GMapMap
        :center='DEFAULT_CAMPUS_LATLNG'
        :zoom='17'
        class='mapCanvas tallMap'
        id="campusMap"
        ref='mapRef'
        style="width: 100%; height: 100vh"
        :options='{
          styles: getStyles(),
          fullscreenControl: false,
          mapTypeControl: false,
          mapTypeControlOptions: {
            position: google && google.maps.ControlPosition.TOP_LEFT,
            style: google && google.maps.MapTypeControlStyle.DROPDOWN_MENU
          },
          streetViewControl: true,
          zoomControl: true
        }' 
      >
      <!-- FEATURE MARKER -->
        <GMapMarker v-if="currentFeature !== null && currentFeature.featureId !== null"
          ref="nodeMarker"
          :position="{lat: Number(currentFeature.lat), lng:Number(currentFeature.lng)}"
          :clickable="true"
          :draggable="true"
          @click="openInfoWindow(currentFeature.featureId)"
          @dragend="updateFeaturePos($event);"
        >
          <GMapInfoWindow @closeclick="openInfoWindow(null)" :closeclick="true" :opened="currentFeature.featureId == openedInfoWindow">
            <div>
              {{ currentFeature.featureName }}
            </div>
          </GMapInfoWindow>
        </GMapMarker>

      </GMapMap>
    </div>

  </div>
</template>
  
<script setup>
import { onMounted, ref, watch } from 'vue'
import axios from 'axios'

const DEFAULT_CAMPUS_LATLNG = {lat: 55.872336, lng: -4.288118}

const featureTypes = ref(null)
const currentFeatureType = ref(null)
const currentFeature = ref(null)
const features = ref(null)
const openedInfoWindow = ref(null)

const mapRef = ref(null)
const map = ref(null)
const google = ref(null)

onMounted(() => {
  getFeatureTypes()

  mapRef.value.$gmapApiPromiseLazy().then((gmapApi) => {
    console.log('gmapApi promise fulfilled')
    google.value = gmapApi
  }).then(() => {
    mapRef.value.$mapPromise.then((mapResponse) => {
    // Wait for Google Maps to load
      map.value = mapResponse
    })
  })
})

watch(() => currentFeatureType.value,
() => {
  getAFeature()
})

/**
   * Method to open/track info windows for the node markers.
*/
function openInfoWindow (nodeId) { 
  openedInfoWindow.value = nodeId
}

function featureTypeChanged () {
  console.log('feature type changed')
  if (currentFeature.value?.featureId !== 0) {
    currentFeature.value = null
  }
}

function featureChanged () {
  console.log('feature changed')
  if (currentFeature.value !== null) {
    map.value.panTo(new google.value.maps.LatLng(currentFeature.value.lat, currentFeature.value.lng))
  } else {
    map.value.panTo(new google.value.maps.LatLng(DEFAULT_CAMPUS_LATLNG.lat, DEFAULT_CAMPUS_LATLNG.lng))
  }
}

function saveCurrentFeature() {
  axios.put('map/admin/features', currentFeature.value)
  .then(response => {
    if (response.status == 200) {
      alert('Update successful.')
    } else if (response.status == 401 || response.status == 403) {
      alert('You are not authenticated. Check console for details.')
    } else {
      alert('Something unexpected happened. Check console for details')
    }
    if(features.value != null) {
      var index = features.value.indexOf(currentFeature.value)
      features.value[index] = response.data
    }
    currentFeature.value = response.data
    currentFeature.value.newish = false
  })
}

function clearCurrentFeature () {
  if (confirm('Cancel current feature edit?')) {
    currentFeature.value = null
    featureChanged()
    getAFeature()
  }
}

function deleteCurrentFeature () {
  if(confirm("Are you sure you want to delete the current feature?")) {
    axios.delete("map/admin/features/" + currentFeature.value.featureId)
      .then((response) => {
        if (response.status == 200) {
          alert('Feature successfully deleted.')
        } else if (response.status == 401 || response.status == 403) {
          alert('You are not authenticated. Check console for details.')
        } else {
          alert('Something unexpected happened. Check console for details')
        }
        currentFeature.value = null
        featureChanged()
        getAFeature()
      }
    )
  }    
}

function createNewFeature () {
  axios.post("map/admin/features")
    .then((response) => {
      currentFeature.value = response.data
      currentFeature.value.newish = true
      currentFeature.value.featureType = currentFeatureType.value
      currentFeature.value.lat = DEFAULT_CAMPUS_LATLNG.lat
      currentFeature.value.lng = DEFAULT_CAMPUS_LATLNG.lng
      if(features.value != null) {
        features.value.push(currentFeature.value)     
      }        
      featureChanged()
    }
  ) 
}

function getFeatureTypes() {
  axios.get('map/admin/features/featuretypes')
  .then(response => {
    featureTypes.value = response.data
    currentFeatureType.value = featureTypes.value[0]
  })
}

function getAFeature() {
  if (currentFeatureType.value !== null) {
    var url = 'map/admin/features/' + currentFeatureType.value
    axios.get(url)
    .then(response => {
      features.value = response.data
    })
  }
}

function updateFeaturePos(event) {
  // update currentFeature position
  currentFeature.value.lat = Number(event.latLng.lat())
  currentFeature.value.lng = Number(event.latLng.lng())

  // Update the value in the features array
  const updatedFeature = features.value.find((ftr) => ftr.featureId == currentFeature.value.featureId )
  updatedFeature.lat = currentFeature.value.lat.toString()
  updatedFeature.lng = currentFeature.value.lng.toString()
}

/**
  * Styles config for the Gmap plugin. 
*/
function getStyles () {
  return [
    { featureType: 'road', elementType: 'geometry', stylers: [ { lightness: 100 }, { visibility: 'simplified' } ] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [ { color: '#575656' } ] },
    { featureType: 'road', elementType: 'labels.text.stroke', stylers: [ { color: '#FFFFFF' } ] },
    { featureType: 'poi', elementType: 'labels', stylers: [ { visibility: 'off' } ] } 
  ]
}
</script>

<style scoped>
.featureAdminContainer {
  display: flex;
}

.featureOptionsContainer {
  padding: 25px 0px 10px 10px;
  width: 60%;
}

.gmapContainer {
  width: 75%;
  padding: 20px;
  height: fit-content;
}
.mapViewContainer {
  display: flex;
}

.mapControls {
  width: 25%;
  padding: 25px 10px 10px 10px;
}

.listHeader {
  color: #003865;
  font-weight: bold;
}

.hrhalfheight {
  width: 75%;
  margin: auto;
}

.feature-list {
  margin-bottom: 15px;
}

.showFeatures, .showFeatures button {
  width: 100%;
}

.featuresContainer {
  margin-top: 15px;
}

.optionRow {
  display: flex;
  margin: 10px 0px 0px 5px;
  align-items: center;
  justify-content: space-between;
}

.optionRow > :nth-child(1) {
  margin-right: 15px;
  width: 25%;
  text-align: left;
  word-break: break-word;
  font-size: 13px;
  font-weight: bold;
}

.optionRow > :nth-child(2) {
  flex: 1;
  width: 100% !important;
}

</style>